import React from 'react';
import PropTypes from 'prop-types'
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

const LoadingWrapper = styled('div')`
	${props => props.cover === 'content' ? `
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
	` : '' }

	${props => props.cover === 'page' ? `
		position: fixed;
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
	` : '' }
`

const VisuallyHidden = styled('span')`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	overflow: hidden;
	clip: rect(0 0 0 0);
	white-space: nowrap;
	border: 0;
`

// Sunday "S" mark, same geometry as the sidebar logo (public/img/icons/sunday-closed-logo.svg).
// Both halves are rotationally symmetric and start at the centre junction, so drawing
// them together reads as one gesture. Measured getTotalLength() of each: ~75.10.
const S_PATHS = [
	'M4.37536 21.6942L8.30006 17.7695L7.47861 16.9481C4.87736 14.3468 4.87736 10.1027 7.47861 7.50143C10.0799 4.90017 14.324 4.90017 16.9253 7.50143L17.7467 8.32287L21.6714 4.39817L20.85 3.57673C16.081 -1.19224 8.3457 -1.19224 3.57673 3.57673C-1.19224 8.34569 -1.19224 16.081 3.57673 20.85L4.37536 21.6942Z',
	'M18.5454 9.1442L17.7239 8.32275L13.7992 12.2475L14.6207 13.0689C15.8757 14.3239 16.5602 15.9896 16.5602 17.7922C16.5602 19.572 15.8757 21.2606 14.6207 22.5156C13.3657 23.7706 11.7 24.4551 9.89736 24.4551C8.11755 24.4551 6.42901 23.7706 5.17402 22.5156L4.35257 21.6941L0.450684 25.596L1.27213 26.4174C3.57676 28.7221 6.63437 29.9999 9.92017 29.9999C13.1832 29.9999 16.2636 28.7221 18.5682 26.4174C20.8728 24.1128 22.1506 21.0552 22.1506 17.7694C22.1506 14.4836 20.85 11.4488 18.5454 9.1442Z',
]
const PATH_LENGTH = 75.2

/*
 * One 2.6s timeline shared by every layer, so nothing can drift out of phase:
 *   0     -> 1.25s  stroke draws the outline once (ease-in-out)
 *   1.10s -> 1.46s  fill fades in (stroke stays underneath, same ink)
 *   1.46s -> 1.90s  hold
 *   1.90s -> 2.34s  whole mark fades out
 *   2.34s -> 2.60s  rest (stroke resets while invisible), loop
 */
const SVGLoader = styled('div')`
	display: inline-block;
	width: 48px;
	height: 65px;
	color: #0E0E10;

	svg {
		display: block;
		width: 100%;
		height: 100%;
		overflow: visible;
	}

	.sunday-loader__mark {
		animation: sunday-loader-mark 2.6s linear infinite;
	}

	.sunday-loader__stroke path {
		fill: none;
		stroke: currentColor;
		stroke-width: 0.8;
		stroke-linejoin: round;
		stroke-dasharray: ${PATH_LENGTH};
		stroke-dashoffset: ${PATH_LENGTH};
		animation: sunday-loader-draw 2.6s linear infinite;
	}

	.sunday-loader__fill {
		fill: currentColor;
		opacity: 0;
		animation: sunday-loader-fill 2.6s linear infinite;
	}

	@keyframes sunday-loader-draw {
		0% {
			stroke-dashoffset: ${PATH_LENGTH};
			animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1);
		}
		48%, 100% {
			stroke-dashoffset: 0;
		}
	}

	@keyframes sunday-loader-fill {
		0%, 42% {
			opacity: 0;
			animation-timing-function: cubic-bezier(0.33, 0, 0.2, 1);
		}
		56%, 100% {
			opacity: 1;
		}
	}

	@keyframes sunday-loader-mark {
		0%, 73% {
			opacity: 1;
			animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1);
		}
		90%, 100% {
			opacity: 0;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.sunday-loader__stroke {
			display: none;
		}

		.sunday-loader__fill {
			opacity: 1;
			animation: none;
		}

		.sunday-loader__mark {
			animation: sunday-loader-pulse 2.4s ease-in-out infinite alternate;
		}
	}

	@keyframes sunday-loader-pulse {
		from { opacity: 1; }
		to { opacity: 0.55; }
	}
`

const Loading = (props) => {
	const { align, cover = 'content' } = props
	const { t } = useTranslation(undefined, { useSuspense: false })
	return (
		<LoadingWrapper
			className={`${align ? `text-${align}` : ''}`}
			cover={cover}
			role="status"
			aria-live="polite"
		>
			<SVGLoader>
				<svg viewBox="-0.5 -0.5 24 31" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
					<g className="sunday-loader__mark">
						<g className="sunday-loader__fill">
							{S_PATHS.map((d) => <path key={d} d={d} />)}
						</g>
						<g className="sunday-loader__stroke">
							{S_PATHS.map((d) => <path key={d} d={d} />)}
						</g>
					</g>
				</svg>
			</SVGLoader>
			<VisuallyHidden>{t('common.loading')}</VisuallyHidden>
		</LoadingWrapper>
	)
}

Loading.propTypes = {
	size: PropTypes.string,
	cover: PropTypes.string
}

Loading.defaultProps = {
	align: 'center',
	cover: 'inline'
};

export default Loading
